/* global define window document FormData screen */

define([
  'redux',
  'axios',
  'immutable',
  'js-cookie',
  'underscore',
  'es6!launcher/actions/DiscountActions',
  'es6!lib/sumome/react/util/sentryUtils',
], (redux, axios, Immutable, jscookie, _, Actions, { captureAPIError }) => {
  function getRequestConfig() {
    return {
      withCredentials: false,
      headers: {
        'X-Sumo-Auth': jscookie.get('__smToken'),
      },
    };
  }

  function _receiveDiscountSettings(settings) {
    return {
      type: Actions.RECEIVE_DISCOUNT_SETTINGS,
      settings,
    };
  }

  function _receiveDiscounts(discounts, campaignId, tier) {
    return {
      type: Actions.RECEIVE_DISCOUNTS,
      discounts,
      campaignId,
      tier,
    };
  }

  function _receiveDiscountCampaigns(discountCampaigns, tier) {
    return {
      type: Actions.RECEIVE_DISCOUNT_CAMPAIGNS,
      discountCampaigns,
      tier,
    };
  }

  function clearDiscountCampaigns() {
    return {
      type: Actions.CLEAR_DISCOUNT_CAMPAIGNS,
    };
  }

  function deleteDiscountCampaigns(campaignIds, selectAll) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      axios.post(`${sumoUrl}/apps/discounts/delete`, {
        site_id: siteId,
        campaign_id: campaignIds,
        select_all: selectAll,
      }, getRequestConfig()).then((res) => {
        const data = res.data;

        if (!data.success) {
          if (data.message) {
            console.log(data.message);
            return Promise.reject(data.message);
          }

          throw new Error('Failed to delete discount campaigns.');
        }

        return dispatch(fetchDiscountCampaigns());
      }).catch(error => {
        captureAPIError(error);
      });
    };
  }

  function updateDiscountSetting(setting, value) {
    return {
      type: Actions.UPDATE_DISCOUNT_SETTING,
      setting,
      value,
    };
  }

  function fetchDiscountDownloadProgress(downloadId) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      axios.post(`${sumoUrl}/list/export/status`, {
          site_id: siteId,
          download_id: downloadId,
        }, getRequestConfig())

        .then(res => dispatch(_receiveDiscountDownloadProgress(res.data)))

        .catch(error => {
          captureAPIError(error);
        });
    }
  }

  function fetchAllDiscountCampaigns() {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      return axios.post(`${sumoUrl}/apps/discounts/campaigns`, {
        site_id: siteId,
      }, getRequestConfig()).then((res) => {
        const data = res.data;

        if (!data.success) {
          if (data.message) {
            console.log(data.message);
            return Promise.reject(data.message);
          }

          throw new Error('Failed to fetch discount campaigns.');
        }

        return dispatch(updateDiscountSetting('allCampaigns', Immutable.fromJS(data.campaigns)));
      }).catch((err) => {
        captureAPIError(err);
        return Promise.reject(err);
      });
    };
  }

  function fetchDiscountSettings() {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      return axios.post(`${sumoUrl}/apps/discounts/settings`, {
        site_id: siteId,
      }, getRequestConfig()).then((res) => {
        const data = res.data;

        if (!data.success) {
          if (data.message) {
            console.log(data.message);
            return Promise.reject(data.message);
          }

          throw new Error('Failed to fetch discount settings.');
        }

        dispatch(_receiveDiscountSettings(data.settings));
        return Promise.resolve(data.settings);
      }).catch((err) => {
        captureAPIError(err);
        return Promise.reject(err);
      });
    };
  }

  function fetchDiscounts(campaignId) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');
      const limit = getState().discounts.get('discountsLimit');
      const page = getState().discounts.get('discountsPage');
      const filter = getState().discounts.get('discountsFilter');

      dispatch(updateDiscountSetting('loading', true));
      return axios.post(`${sumoUrl}/apps/discounts/load`, {
        site_id: siteId,
        campaign_id: campaignId,
        limit,
        page,
        filter,
      }, getRequestConfig()).then((res) => {
        const data = res.data;

        dispatch(updateDiscountSetting('loading', false));

        if (!data.success) {
          if (data.message) {
            console.log(data.message);
            return Promise.reject(data.message);
          }

          throw new Error('Failed to fetch discounts.');
        }

        return Promise.all([
          dispatch(_receiveDiscounts(data.discounts, campaignId, data.tier)),
          dispatch(updateDiscountSetting('discountsTotal', data.total)),
        ]);
      }).catch((err) => {
        captureAPIError(err);
        return Promise.reject(err);
      });
    };
  }

  function fetchDiscountCampaigns(setLoading = true) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');
      const limit = getState().discounts.get('campaignsLimit');
      const page = getState().discounts.get('campaignsPage');
      const textFilter = getState().discounts.get('textFilter');

      if (setLoading) {
        dispatch(updateDiscountSetting('loading', true));
      }

      return axios.post(`${sumoUrl}/apps/discounts/campaigns`, {
        site_id: siteId,
        text_filter: textFilter,
        page,
        limit,
      }, getRequestConfig()).then((res) => {
        const data = res.data;

        dispatch(updateDiscountSetting('loading', false));

        if (!data.success) {
          if (data.message) {
            console.log(data.message);
            return Promise.reject(data.message);
          }

          throw new Error('Failed to fetch discount campaigns.');
        }

        // Refetch campaigns if any campaigns are still creating discount codes
        const savingCampaign = _.findWhere(data.campaigns, { saving: true });
        if (savingCampaign) {
          setTimeout(() => dispatch(fetchDiscountCampaigns(false)), 2000);
        }

        return Promise.all([
          dispatch(_receiveDiscountCampaigns(data.campaigns, data.tier)),
          dispatch(updateDiscountSetting('campaignsTotal', data.total)),
        ]);
      }).catch((err) => {
        captureAPIError(err);
        return Promise.reject(err);
      });
    };
  }

  function saveDiscounts(options) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      if (!options || !options.campaignName) return Promise.reject('Invalid options');

      dispatch(updateDiscountSetting('loading', true));
      return axios.post(`${sumoUrl}/apps/discounts/save`, {
        site_id: siteId,
        options,
      }, getRequestConfig()).then((res) => {
        const data = res.data;

        dispatch(updateDiscountSetting('loading', false));
        if (!data.success) {
          if (data.message) {
            dispatch(updateDiscountSetting('error', data.message));
            return Promise.reject(data.message);
          }

          throw new Error('Failed to save discount codes.');
        }

        return Promise.all([
          dispatch(fetchAllDiscountCampaigns()),
          dispatch(fetchDiscountCampaigns()),
        ]).then(() => Promise.resolve(res.data.discountCampaignId));
      }).catch((err) => {
        captureAPIError(err);
        return Promise.reject(err);
      });
    };
  }

  function updateDiscountCampaign(options) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      if (!options || !options.campaignName) return Promise.reject('Invalid options');

      const {
        campaignId,
        campaignName,
        startDate,
        endDate,
      } = options;

      dispatch(updateDiscountSetting('loading', true));
      return axios.post(`${sumoUrl}/apps/discounts/campaign/save`, {
        site_id: siteId,
        campaign_id: campaignId,
        campaign_name: campaignName,
        start_date: startDate,
        end_date: endDate,
      }, getRequestConfig()).then((res) => {
        const data = res.data;

        dispatch(updateDiscountSetting('loading', false));
        if (!data.success) {
          if (data.message) {
            console.log(data.message);
            return Promise.reject(data.message);
          }

          throw new Error('Failed to save discount campaign.');
        }

        return dispatch(fetchDiscountCampaigns());
      }).catch((err) => {
        captureAPIError(err);
        return Promise.reject(err);
      });
    };
  }

  function addDiscountCodes(campaignId, options) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      if (!options) return Promise.reject('Invalid options');

      dispatch(updateDiscountSetting('loading', true));
      return axios.post(`${sumoUrl}/apps/discounts/add`, {
        site_id: siteId,
        campaign_id: campaignId,
        options,
      }, getRequestConfig()).then((res) => {
        const data = res.data;

        if (!data.success) {
          if (data.message) {
            console.log(data.message);
            return Promise.reject(data.message);
          }

          throw new Error('Failed to save discount campaign.');
        }

        return Promise.all([
          dispatch(fetchDiscountCampaigns()),
          dispatch(fetchDiscounts(campaignId)),
        ]);
      }).catch((err) => {
        captureAPIError(err);
        return Promise.reject(err);
      });
    };
  }

  function integrateShopify(shopName) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');
      const shopifyAccessToken = getState().discounts.get('shopifyAccessToken');

      const reloadDiscountSettings = () => {
        window.removeEventListener('focus', reloadDiscountSettings);
        return new Promise((resolve) => {
          dispatch(fetchDiscountSettings()).then((settings) => {
            if (
              settings &&
              settings.shopifyAccessToken &&
              settings.shopifyAccessTokenValid &&
              !shopifyAccessToken
            ) {
              dispatch(clearDiscountCampaigns());
              return resolve(true);
            }
            return resolve(false);
          });
        });
      };

      const width = 540;
      const height = 640;
      const left = (screen.width / 2) - (width / 2);
      const top = (screen.height / 2) - (height / 2);

      const returnUrl = encodeURIComponent('/apps/discounts/plugin/integrate/callback');
      const shopifyUrl = encodeURIComponent(`https://${shopName.replace(/\.myshopify\.com/g, '')}.myshopify.com`);
      const url = `${sumoUrl}/plugin/connect?site_id=${siteId}&return_url=${returnUrl}&shopify_url=${shopifyUrl}`;

      window.open(url, 'Shopify Connect', `height=${height},width=${width},top=${top},left=${left}`);

      return new Promise((resolve) => {
        setTimeout(() => window.addEventListener('focus', () => {
          reloadDiscountSettings().then(resolve);
        }), 1000);
      });
    };
  }

  function integrateWooCommerce(shopName) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');
      const wooCommerceAccessToken = getState().discounts.get('wooCommerceAccessToken');

      const width = 540;
      const height = 640;
      const left = (screen.width / 2) - (width / 2);
      const top = (screen.height / 2) - (height / 2);
      const win = window.open('', 'WooCommerce Connect', `height=${height},width=${width},top=${top},left=${left}`);
      win.location.href = `${sumoUrl}/apps/discounts/woocommerce/integrate?site_id=${siteId}&shop=${shopName}`;

      const reloadDiscountSettings = () => {
        window.removeEventListener('focus', reloadDiscountSettings);
        dispatch(fetchDiscountSettings()).then((settings) => {
          if (settings && settings.wooCommerceAccessToken && settings.wooCommerceAccessTokenValid && !wooCommerceAccessToken) {
            dispatch(clearDiscountCampaigns());
          }
        });
      };

      setTimeout(() => window.addEventListener('focus', reloadDiscountSettings), 1000);
    };
  }

  function disconnectIntegration(service) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      return axios.post(`${sumoUrl}/oaconsumer/${service}/disconnect`, {
        site_id: siteId,
      }, getRequestConfig()).then(() => (
        Promise.all([
          dispatch(fetchDiscountSettings()),
          dispatch(fetchDiscountCampaigns()),
        ])
      )).catch((err) => {
        captureAPIError(err);
        return Promise.reject(err);
      });
    };
  }

  return {
    deleteDiscountCampaigns,
    fetchDiscountSettings,
    fetchDiscountDownloadProgress,
    updateDiscountSetting,
    fetchAllDiscountCampaigns,
    fetchDiscounts,
    fetchDiscountCampaigns,
    saveDiscounts,
    updateDiscountCampaign,
    addDiscountCodes,

    integrateShopify,
    integrateWooCommerce,
    disconnectIntegration,
  };
});

